import React, { useState, useEffect, useMemo } from 'react';
import { useLayout } from '../../../context/LayoutContext';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Tooltip,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Breadcrumbs,
  Link
} from '@mui/material';
import { 
  ArrowBackIos,
  ArrowForwardIos,
  Today,
  FilterAlt,
  FilterAltOff,
  Info,
  CalendarViewMonth,
  CalendarToday,
  NavigateNext,
  Groups,
  Print
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {  
  useGetMonthlyShiftsByWardQuery,
  useGetYearlyShiftsByWardQuery
} from '../../../slices/shiftApiSlice';
import { useGetUsersQuery } from '../../../slices/usersApiSlice';
import { useGetHospitalLocationsQuery } from '../../../slices/hospitalLocationApiSlice';
import { useSelector } from 'react-redux';

import { getLocalDateString } from '../../../utils/dateUtils';

// Add print styles component
const PrintStyles = () => (
  <style>{`
    @media print {
      /* Hide non-essential elements */
      .no-print {
        display: none !important;
      }
      
      /* Ensure proper page breaking */
      .print-page {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      
      /* Ensure colors are preserved in print */
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      
      /* Fix for Firefox background colors */
      @-moz-document url-prefix() {
        .day-card, .month-card, .summary-card, .header-cell {
          box-shadow: inset 0 0 0 1000px var(--print-color) !important;
        }
      }
      
      /* Print-specific adjustments */
      body {
        margin: 0 !important;
        padding: 0 !important;
        background: white !important;
      }
      
      .print-container {
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      
      /* Make text black for better readability in print */
      .print-text {
        color: #000 !important;
        font-weight: bold !important;
      }
      
      /* Larger text for print */
      .print-text {
        font-size: 11pt !important;
        line-height: 1.4 !important;
      }
      
      /* Page margins */
      @page {
        margin: 0.5in !important;
      }
      
      /* Avoid page breaks inside important sections */
      .calendar-row {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      
      /* Header information */
      .print-header {
        text-align: center !important;
        margin-bottom: 20px !important;
        border-bottom: 2px solid #000 !important;
        padding-bottom: 10px !important;
      }
      
      /* Make sure colors are visible */
      .filled-cell {
        background-color: #4caf50 !important;
        color: white !important;
      }
      
      .available-cell {
        background-color: #2196f3 !important;
        color: white !important;
      }
      
      .weekend-cell {
        background-color: rgba(255, 152, 0, 0.1) !important;
      }
      
      .not-in-month-cell {
        background-color: #f5f5f5 !important;
        color: #999 !important;
      }
      
      /* Calendar grid */
      .calendar-grid {
        display: grid !important;
        grid-template-columns: repeat(7, 1fr) !important;
        gap: 4px !important;
        margin-top: 20px !important;
      }
      
      .calendar-day {
        border: 1px solid #ddd !important;
        border-radius: 4px !important;
        padding: 8px !important;
        min-height: 100px !important;
        page-break-inside: avoid !important;
      }
      
      .day-number {
        font-weight: bold !important;
        font-size: 14px !important;
        margin-bottom: 4px !important;
      }
      
      .shift-hours {
        font-size: 10px !important;
        margin-top: 4px !important;
      }
      
      .hours-label {
        font-size: 9px !important;
        font-weight: normal !important;
      }
      
      /* Year view grid */
      .year-grid {
        display: grid !important;
        grid-template-columns: repeat(4, 1fr) !important;
        gap: 12px !important;
        margin-top: 20px !important;
      }
      
      .year-month-card {
        border: 1px solid #ddd !important;
        border-radius: 4px !important;
        padding: 12px !important;
        min-height: 120px !important;
        page-break-inside: avoid !important;
      }
      
      .month-name {
        font-weight: bold !important;
        font-size: 14px !important;
        margin-bottom: 8px !important;
        background-color: #1976d2 !important;
        color: white !important;
        padding: 4px !important;
        text-align: center !important;
        border-radius: 3px !important;
      }
      
      /* Summary table */
      .summary-table {
        width: 100% !important;
        border-collapse: collapse !important;
        margin-top: 20px !important;
        font-size: 10px !important;
      }
      
      .summary-table th,
      .summary-table td {
        border: 1px solid #ddd !important;
        padding: 8px !important;
        text-align: center !important;
      }
      
      .summary-table th {
        background-color: #f5f5f5 !important;
        font-weight: bold !important;
      }
    }
  `}</style>
);

// Helper function to format hours and minutes
const formatHoursMinutes = (totalMinutes) => {
  if (!totalMinutes || totalMinutes === 0) return '0h 0m';
  
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

// Helper function to get days in month
const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

// Generate month names
const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Generate years (from current year - 5 to current year + 5)
const generateYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 5; i <= currentYear + 5; i++) {
    years.push(i);
  }
  return years;
};

// Helper function to get week containing a specific date (Monday start)
const getWeekForDate = (date) => {
  const current = new Date(date);
  const day = current.getDay();
  const diff = current.getDate() - day + (day === 0 ? -6 : 1); // Monday
  
  const monday = new Date(current.setDate(diff));
  return monday;
};

// Helper function to format date for URL query
const formatDateForUrl = (date) => {
  return date.toISOString().split('T')[0];
};

const MonthlyCalendarView = () => {
  const { setFluid } = useLayout();
  const navigate = useNavigate();
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  // Set default to current month
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth());
  const [selectedWardFilter, setSelectedWardFilter] = useState('all');
  // Initialize directorate filter as null initially, will be set after data loads
  const [selectedDirectorateFilter, setSelectedDirectorateFilter] = useState(null);
  const [selectedJobTitleFilter, setSelectedJobTitleFilter] = useState('all');
  const [wards, setWards] = useState(['all']);
  const [directorates, setDirectorates] = useState(['all']);
  const [jobTitles, setJobTitles] = useState(['all']);
  const [filteredWards, setFilteredWards] = useState(['all']);
  const [monthlyData, setMonthlyData] = useState({});
  const [yearlyData, setYearlyData] = useState({});
  const [calendarDays, setCalendarDays] = useState([]);
  // Set default to 'month'
  const [viewMode, setViewMode] = useState('month');
  
  const [totalShifts, setTotalShifts] = useState(0);
  const [totalFilledHours, setTotalFilledHours] = useState(0);
  const [totalAvailableHours, setTotalAvailableHours] = useState(0);
  
  const { userInfo } = useSelector((state) => state.auth);
  
  // Fetch data
  const { data: staffData, isLoading: loadingStaff } = useGetUsersQuery();
  const { data: hospitalLocationsData, isLoading: loadingHospitalLocations } = useGetHospitalLocationsQuery();

  // Extract manager's directorate from userInfo
  const managerDirectorate = useMemo(() => {
    return userInfo?.directorateName || null;
  }, [userInfo]);

  // Calculate month start and end dates for monthly view
  const monthInfo = useMemo(() => {
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    const startDate = new Date(selectedYear, selectedMonth, 1);
    const endDate = new Date(selectedYear, selectedMonth, daysInMonth);
    
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      daysInMonth,
      monthName: monthNames[selectedMonth],
      year: selectedYear
    };
  }, [selectedYear, selectedMonth]);
  
  // Calculate year start and end dates for yearly view
  const yearInfo = useMemo(() => {
    const startDate = new Date(selectedYear, 0, 1);
    const endDate = new Date(selectedYear, 11, 31);
    
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      year: selectedYear
    };
  }, [selectedYear]);

  // Now we can safely use monthInfo and yearInfo in the queries
  const { data: monthlyShiftsData, isLoading: loadingMonthlyShifts } = useGetMonthlyShiftsByWardQuery({
    startDate: monthInfo?.startDate,
    endDate: monthInfo?.endDate,
    ward: selectedWardFilter === 'all' ? '' : selectedWardFilter,
    jobTitle: selectedJobTitleFilter === 'all' ? '' : selectedJobTitleFilter
  }, {
    skip: !monthInfo?.startDate || viewMode === 'year'
  });

  const { data: yearlyShiftsData, isLoading: loadingYearlyShifts } = useGetYearlyShiftsByWardQuery({
    startDate: yearInfo.startDate,
    endDate: yearInfo.endDate,
    ward: selectedWardFilter === 'all' ? '' : selectedWardFilter,
    jobTitle: selectedJobTitleFilter === 'all' ? '' : selectedJobTitleFilter
  }, {
    skip: viewMode === 'month'
  });

  // Create ward-to-directorate mapping
  const wardToDirectorateMap = useMemo(() => {
    if (!hospitalLocationsData) return {};
    
    const map = {};
    hospitalLocationsData.forEach(location => {
      if (location.wardDepartmentArea && location.directorate) {
        map[location.wardDepartmentArea] = location.directorate;
      }
    });
    return map;
  }, [hospitalLocationsData]);

  // Extract unique directorates, wards, and job titles
  useEffect(() => {
    if (hospitalLocationsData) {
      // Extract unique directorates
      const uniqueDirectorates = [...new Set(hospitalLocationsData
        .map(location => location.directorate)
        .filter(directorate => directorate && directorate.trim() !== '')
      )].sort();
      
      setDirectorates(['all', ...uniqueDirectorates]);
      
      // Extract unique wards
      const uniqueWards = [...new Set(hospitalLocationsData
        .map(location => location.wardDepartmentArea)
        .filter(ward => ward && ward.trim() !== '')
      )].sort();
      
      setWards(['all', ...uniqueWards]);
      setFilteredWards(['all', ...uniqueWards]);
    }
    
    if (staffData) {
      // Extract unique job titles
      const titles = [...new Set(staffData
        .filter(user => !user.isAdmin && !user.isAgency)
        .map(user => user.jobtitle)
        .filter(title => title && title.trim() !== '')
      )].sort();
      
      setJobTitles(['all', ...titles]);
    }
  }, [hospitalLocationsData, staffData]);

  // Set default directorate filter after directorates list is loaded
  useEffect(() => {
    // Only run this effect if we have directorates loaded (more than just 'all')
    if (directorates.length > 1) {
      console.log('Directorates loaded:', directorates);
      console.log('Manager directorate:', managerDirectorate);
      
      if (managerDirectorate) {
        // Check if manager's directorate exists in the directorates list
        const directorateExists = directorates.some(
          dir => dir !== 'all' && dir === managerDirectorate
        );
        
        console.log('Directorate exists in list:', directorateExists);
        
        if (directorateExists) {
          console.log('Setting directorate filter to:', managerDirectorate);
          setSelectedDirectorateFilter(managerDirectorate);
        } else {
          // If manager's directorate not found, default to 'all'
          console.log('Manager directorate not found, defaulting to all');
          setSelectedDirectorateFilter('all');
        }
      } else {
        // If no manager directorate found, default to 'all'
        console.log('No manager directorate found, defaulting to all');
        setSelectedDirectorateFilter('all');
      }
    }
  }, [directorates, managerDirectorate]);

  // Generate calendar days array (1-31) for monthly view
  useEffect(() => {
    const daysArray = [];
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    
    for (let day = 1; day <= 31; day++) {
      const isInMonth = day <= daysInMonth;
      
      // Create date in local timezone
      const date = isInMonth ? 
        new Date(selectedYear, selectedMonth, day) : 
        null;
      
      // Get date string in YYYY-MM-DD format
      const dateString = date ? getLocalDateString(date.toISOString()) : null;
      
      if (date) {
        const dayOfWeek = date.getDay();
        const isWeekend = isInMonth ? (dayOfWeek === 0 || dayOfWeek === 6) : false;
        
        daysArray.push({
          dayNumber: day,
          date: dateString,
          isInMonth,
          isWeekend,
          dayName: isInMonth ? 
            ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayOfWeek] : 
            ''
        });
      } else {
        daysArray.push({
          dayNumber: day,
          date: null,
          isInMonth: false,
          isWeekend: false,
          dayName: ''
        });
      }
    }
    
    setCalendarDays(daysArray);
  }, [selectedYear, selectedMonth]);

  // Filter wards based on selected directorate
  useEffect(() => {
    if (selectedDirectorateFilter && selectedDirectorateFilter !== 'all' && hospitalLocationsData) {
      const filteredWardsList = hospitalLocationsData
        .filter(location => location.directorate === selectedDirectorateFilter)
        .map(location => location.wardDepartmentArea)
        .filter(ward => ward && ward.trim() !== '')
        .sort();
      
      const uniqueFilteredWards = ['all', ...new Set(filteredWardsList)];
      setFilteredWards(uniqueFilteredWards);
      
      if (selectedWardFilter !== 'all' && !filteredWardsList.includes(selectedWardFilter)) {
        setSelectedWardFilter('all');
      }
    } else if (hospitalLocationsData) {
      const allWards = ['all', ...new Set(hospitalLocationsData
        ?.map(location => location.wardDepartmentArea)
        .filter(ward => ward && ward.trim() !== '') || []
      )].sort();
      
      setFilteredWards(allWards);
    }
  }, [selectedDirectorateFilter, hospitalLocationsData, selectedWardFilter]);

  // Process monthly data
  useEffect(() => {
    if (monthlyShiftsData && viewMode === 'month') {
      const dataByDay = {};
      let totalShiftsCount = 0;
      let totalFilledMinutes = 0;
      let totalAvailableMinutes = 0;
      
      calendarDays.forEach(day => {
        if (day.isInMonth) {
          dataByDay[day.date] = {
            filledHours: 0,
            availableHours: 0,
            filledShifts: 0,
            availableShifts: 0,
            filledMinutes: 0,
            availableMinutes: 0
          };
        }
      });
      
      // Filter shifts based on directorate
      const filteredShifts = monthlyShiftsData.filter(shift => {
        // If no directorate filter or 'all', include all shifts
        if (!selectedDirectorateFilter || selectedDirectorateFilter === 'all') return true;
        
        // Get the ward from the shift
        const shiftWard = shift.wardDepartmentArea;
        
        // If we can't determine the ward, exclude the shift
        if (!shiftWard) return false;
        
        // Get the directorate for this ward
        const wardDirectorate = wardToDirectorateMap[shiftWard];
        
        // Include shift if directorate matches
        return wardDirectorate === selectedDirectorateFilter;
      });
      
      filteredShifts.forEach(shift => {
        if (!shift.startDateTime || !shift.endDateTime) return;
        
        const shiftDate = getLocalDateString(shift.startDateTime);
        const isFilled = !!shift.filledBy || !!shift.filledByAgency;
        
        const start = new Date(shift.startDateTime);
        const end = new Date(shift.endDateTime);
        const durationMinutes = Math.round((end - start) / (1000 * 60));
        
        if (dataByDay[shiftDate]) {
          if (isFilled) {
            dataByDay[shiftDate].filledMinutes += durationMinutes;
            dataByDay[shiftDate].filledShifts += 1;
            totalFilledMinutes += durationMinutes;
          } else {
            dataByDay[shiftDate].availableMinutes += durationMinutes;
            dataByDay[shiftDate].availableShifts += 1;
            totalAvailableMinutes += durationMinutes;
          }
          totalShiftsCount += 1;
        }
      });
      
      Object.keys(dataByDay).forEach(date => {
        dataByDay[date].filledHours = dataByDay[date].filledMinutes / 60;
        dataByDay[date].availableHours = dataByDay[date].availableMinutes / 60;
      });
      
      setMonthlyData(dataByDay);
      setTotalShifts(totalShiftsCount);
      setTotalFilledHours(totalFilledMinutes);
      setTotalAvailableHours(totalAvailableMinutes);
    }
  }, [monthlyShiftsData, calendarDays, viewMode, selectedDirectorateFilter, wardToDirectorateMap]);

  // Process yearly data
  useEffect(() => {
    if (yearlyShiftsData && viewMode === 'year') {
      const dataByMonth = {};
      let totalShiftsCount = 0;
      let totalFilledMinutes = 0;
      let totalAvailableMinutes = 0;
      
      monthNames.forEach((monthName, index) => {
        dataByMonth[index] = {
          monthName,
          filledMinutes: 0,
          availableMinutes: 0,
          filledShifts: 0,
          availableShifts: 0,
          filledHours: 0,
          availableHours: 0
        };
      });
      
      // Filter shifts based on directorate
      const filteredShifts = yearlyShiftsData.filter(shift => {
        // If no directorate filter or 'all', include all shifts
        if (!selectedDirectorateFilter || selectedDirectorateFilter === 'all') return true;
        
        // Get the ward from the shift
        const shiftWard = shift.wardDepartmentArea;
        
        // If we can't determine the ward, exclude the shift
        if (!shiftWard) return false;
        
        // Get the directorate for this ward
        const wardDirectorate = wardToDirectorateMap[shiftWard];
        
        // Include shift if directorate matches
        return wardDirectorate === selectedDirectorateFilter;
      });
      
      filteredShifts.forEach(shift => {
        if (!shift.startDateTime || !shift.endDateTime) return;
        
        const shiftDate = new Date(shift.startDateTime);
        const shiftMonth = shiftDate.getMonth();
        const isFilled = !!shift.filledBy || !!shift.filledByAgency;
        
        const start = new Date(shift.startDateTime);
        const end = new Date(shift.endDateTime);
        const durationMinutes = Math.round((end - start) / (1000 * 60));
        
        if (dataByMonth[shiftMonth]) {
          if (isFilled) {
            dataByMonth[shiftMonth].filledMinutes += durationMinutes;
            dataByMonth[shiftMonth].filledShifts += 1;
            totalFilledMinutes += durationMinutes;
          } else {
            dataByMonth[shiftMonth].availableMinutes += durationMinutes;
            dataByMonth[shiftMonth].availableShifts += 1;
            totalAvailableMinutes += durationMinutes;
          }
          totalShiftsCount += 1;
        }
      });
      
      Object.keys(dataByMonth).forEach(monthIndex => {
        dataByMonth[monthIndex].filledHours = dataByMonth[monthIndex].filledMinutes / 60;
        dataByMonth[monthIndex].availableHours = dataByMonth[monthIndex].availableMinutes / 60;
      });
      
      setYearlyData(dataByMonth);
      setTotalShifts(totalShiftsCount);
      setTotalFilledHours(totalFilledMinutes);
      setTotalAvailableHours(totalAvailableMinutes);
    }
  }, [yearlyShiftsData, viewMode, selectedDirectorateFilter, wardToDirectorateMap]);

  // Navigation functions
  const navigateYear = (direction) => {
    setSelectedYear(prev => prev + direction);
    if (selectedMonth !== null) {
      setSelectedMonth(null);
      setViewMode('year');
    }
  };
  
  const navigateMonth = (direction) => {
    if (selectedMonth === null) return;
    
    let newYear = selectedYear;
    let newMonth = selectedMonth + direction;
    
    if (newMonth < 0) {
      newMonth = 11;
      newYear -= 1;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear += 1;
    }
    
    setSelectedYear(newYear);
    setSelectedMonth(newMonth);
  };
  
  const goToCurrentMonth = () => {
    const current = new Date();
    setSelectedYear(current.getFullYear());
    setSelectedMonth(current.getMonth());
    setViewMode('month');
  };
  
  const goToCurrentYear = () => {
    const current = new Date();
    setSelectedYear(current.getFullYear());
    setSelectedMonth(null);
    setViewMode('year');
  };
  
  const handleMonthSelect = (monthIndex) => {
    setSelectedMonth(monthIndex);
    setViewMode('month');
  };
  
  const handleClearFilters = () => {
    setSelectedWardFilter('all');
    // When clearing filters, set directorate back to manager's directorate or 'all'
    if (managerDirectorate) {
      const directorateExists = directorates.some(
        dir => dir !== 'all' && dir === managerDirectorate
      );
      setSelectedDirectorateFilter(directorateExists ? managerDirectorate : 'all');
    } else {
      setSelectedDirectorateFilter('all');
    }
    setSelectedJobTitleFilter('all');
  };

  // Navigate to Staff Weekly View with specific date
  const handleNavigateToStaffWeeklyView = (dateString) => {
    if (!dateString) return;
    
    const date = new Date(dateString);
    const weekStart = getWeekForDate(date);
    
    // Navigate with query parameters
    navigate(`/manager/ward-weekly-schedule?week=${formatDateForUrl(weekStart)}`);
  };

  // Navigate to Staff Weekly View from header button
  const handleNavigateToStaffWeeklyViewFromHeader = () => {
    const currentDate = new Date();
    const weekStart = getWeekForDate(currentDate);
    navigate(`/manager/ward-weekly-schedule?week=${formatDateForUrl(weekStart)}`);
  };

  // Determine if directorate filter should be disabled
  const isDirectorateDisabled = useMemo(() => {
    // Disable if we have a manager directorate AND it's currently selected
    return managerDirectorate && 
           selectedDirectorateFilter === managerDirectorate && 
           selectedDirectorateFilter !== 'all';
  }, [managerDirectorate, selectedDirectorateFilter]);

  // PRINT FUNCTION
  const handlePrint = () => {
    // Create a printable version of the calendar
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      // Show snackbar or alert about popup blocker
      return;
    }
    
    // Get filter summary
    const filterInfo = getFilterSummary();
    
    // Get the data for print
    let printContent = '';
    
    if (viewMode === 'month') {
      // Generate monthly view print
      printContent = generateMonthlyPrintContent(filterInfo);
    } else {
      // Generate yearly view print
      printContent = generateYearlyPrintContent(filterInfo);
    }
    
    // Build the printable HTML
    const printHTML = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Calendar Analytics - ${viewMode === 'month' ? `${monthInfo.monthName} ${monthInfo.year}` : `Year ${selectedYear}`}</title>
        <style>
          @page {
            size: ${viewMode === 'month' ? 'landscape' : 'portrait'};
            margin: 0.5in;
          }
          
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background: white;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          .print-header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }
          
          .print-title {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          
          .print-subtitle {
            font-size: 16px;
            color: #666;
            margin-bottom: 10px;
          }
          
          .print-info {
            font-size: 12px;
            color: #666;
            margin-bottom: 5px;
          }
          
          ${viewMode === 'month' ? `
            .calendar-grid {
              display: grid;
              grid-template-columns: repeat(7, 1fr);
              gap: 4px;
              margin-top: 20px;
            }
            
            .calendar-day {
              border: 1px solid #ddd;
              border-radius: 4px;
              padding: 8px;
              min-height: 100px;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            
            .day-number {
              font-weight: bold;
              font-size: 14px;
              margin-bottom: 4px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            
            .weekend-indicator {
              background-color: #ff9800;
              color: white;
              padding: 2px 4px;
              border-radius: 3px;
              font-size: 9px;
              font-weight: bold;
            }
            
            .shift-hours {
              margin-top: 8px;
            }
            
            .hours-section {
              margin: 4px 0;
              padding: 4px;
              border-radius: 3px;
              font-size: 10px;
              font-weight: bold;
              color: white;
            }
            
            .filled-hours {
              background-color: #4caf50;
            }
            
            .available-hours {
              background-color: #2196f3;
            }
            
            .total-hours {
              background-color: #333;
              color: white;
              margin-top: 8px;
              font-size: 9px;
            }
            
            .no-data {
              text-align: center;
              color: #999;
              font-style: italic;
              font-size: 10px;
              margin-top: 20px;
            }
            
            .weekend-day {
              background-color: rgba(255, 152, 0, 0.05);
            }
            
            .not-in-month {
              background-color: #f5f5f5;
              color: #999;
              font-style: italic;
            }
          ` : `
            .year-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 12px;
              margin-top: 20px;
            }
            
            .month-card {
              border: 1px solid #ddd;
              border-radius: 4px;
              padding: 12px;
              min-height: 120px;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            
            .month-header {
              background-color: #1976d2;
              color: white;
              padding: 8px;
              text-align: center;
              font-weight: bold;
              border-radius: 4px;
              margin-bottom: 10px;
            }
            
            .month-stats {
              font-size: 11px;
            }
            
            .stat-row {
              display: flex;
              justify-content: space-between;
              margin: 4px 0;
              padding: 4px;
              border-radius: 3px;
            }
            
            .filled-stat {
              background-color: #4caf50;
              color: white;
            }
            
            .available-stat {
              background-color: #2196f3;
              color: white;
            }
            
            .total-stat {
              background-color: #333;
              color: white;
              margin-top: 8px;
              font-weight: bold;
            }
          `}
          
          .summary-section {
            margin-top: 30px;
            padding: 20px;
            border: 1px solid #ddd;
            background-color: #f9f9f9;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          .summary-title {
            font-weight: bold;
            font-size: 16px;
            margin-bottom: 10px;
          }
          
          .summary-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
            margin-top: 15px;
          }
          
          .summary-card {
            padding: 15px;
            border-radius: 5px;
            color: white;
            text-align: center;
          }
          
          .summary-total {
            background-color: #673ab7;
          }
          
          .summary-filled {
            background-color: #4caf50;
          }
          
          .summary-available {
            background-color: #2196f3;
          }
          
          .summary-number {
            font-size: 24px;
            font-weight: bold;
            margin: 10px 0;
          }
          
          .summary-label {
            font-size: 12px;
            opacity: 0.9;
          }
          
          .coverage-rate {
            background-color: #ff9800;
            color: white;
            padding: 15px;
            border-radius: 5px;
            text-align: center;
            margin-top: 15px;
          }
          
          .coverage-number {
            font-size: 32px;
            font-weight: bold;
            margin: 10px 0;
          }
          
          .legend {
            margin-top: 30px;
            padding: 15px;
            border: 1px solid #ddd;
            background-color: #f9f9f9;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          .legend-title {
            font-weight: bold;
            margin-bottom: 10px;
            font-size: 14px;
          }
          
          .legend-item {
            display: inline-block;
            margin-right: 20px;
            margin-bottom: 10px;
          }
          
          .legend-color {
            display: inline-block;
            width: 20px;
            height: 20px;
            margin-right: 5px;
            vertical-align: middle;
            border-radius: 3px;
            border: 1px solid #ddd;
          }
          
          .footer {
            margin-top: 30px;
            padding-top: 10px;
            border-top: 1px solid #ddd;
            font-size: 10px;
            color: #666;
            text-align: center;
          }
          
          @media print {
            .no-print {
              display: none !important;
            }
            
            body {
              margin: 0 !important;
              padding: 0 !important;
            }
            
            .calendar-day, .month-card {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
          }
        </style>
      </head>
      <body>
        ${printContent}
        
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() {
              window.close();
            }, 100);
          };
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(printHTML);
    printWindow.document.close();
  };
  
  // Generate monthly view print content
  const generateMonthlyPrintContent = (filterInfo) => {
    // Calculate total filled and available minutes for the month
    let monthTotalFilledMinutes = 0;
    let monthTotalAvailableMinutes = 0;
    let monthTotalShifts = 0;
    
    calendarDays.forEach(day => {
      if (day.isInMonth && day.date) {
        const dayData = monthlyData[day.date] || { filledMinutes: 0, availableMinutes: 0, filledShifts: 0, availableShifts: 0 };
        monthTotalFilledMinutes += dayData.filledMinutes;
        monthTotalAvailableMinutes += dayData.availableMinutes;
        monthTotalShifts += dayData.filledShifts + dayData.availableShifts;
      }
    });
    
    const coverageRate = monthTotalFilledMinutes + monthTotalAvailableMinutes > 0 
      ? Math.round((monthTotalFilledMinutes / (monthTotalFilledMinutes + monthTotalAvailableMinutes)) * 100)
      : 0;
    
    // Organize days into weeks (7 columns)
    const weeks = [];
    let currentWeek = [];
    
    // Find the day of week for the first day of month (0 = Sunday, 1 = Monday, etc.)
    const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1);
    const firstDayOfWeek = firstDayOfMonth.getDay();
    
    // Add empty days for the beginning of the first week
    for (let i = 0; i < firstDayOfWeek; i++) {
      currentWeek.push(null);
    }
    
    calendarDays.forEach((day, index) => {
      if (currentWeek.length === 7) {
        weeks.push([...currentWeek]);
        currentWeek = [];
      }
      
      if (day.isInMonth) {
        currentWeek.push(day);
      } else if (index < 31) {
        currentWeek.push(null);
      }
    });
    
    // Add the last week
    if (currentWeek.length > 0) {
      // Fill remaining days with null if needed
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }
    
    let html = `
      <div class="print-header">
        <div class="print-title">Monthly Calendar Analytics</div>
        <div class="print-subtitle">${monthInfo.monthName} ${monthInfo.year}</div>
        <div class="print-info">Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
        <div class="print-info">Generated By: ${userInfo?.name || 'Manager'}</div>
        ${filterInfo !== 'No filters applied' ? `<div class="print-info">Filters: ${filterInfo}</div>` : ''}
        <div class="print-info">View Mode: Monthly Calendar</div>
      </div>
      
      <div class="calendar-grid">
    `;
    
    // Add day headers
    const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    dayHeaders.forEach(dayName => {
      html += `
        <div style="text-align: center; padding: 8px; background-color: #f0f0f0; font-weight: bold; border-radius: 4px;">
          ${dayName}
        </div>
      `;
    });
    
    // Add calendar days
    weeks.forEach(week => {
      week.forEach((day, index) => {
        if (!day) {
          html += `
            <div class="calendar-day not-in-month">
              <div class="no-data">Not in month</div>
            </div>
          `;
          return;
        }
        
        const dayData = monthlyData[day.date] || { filledMinutes: 0, availableMinutes: 0, filledShifts: 0, availableShifts: 0 };
        const hasData = dayData.filledMinutes > 0 || dayData.availableMinutes > 0;
        
        html += `
          <div class="calendar-day ${day.isWeekend ? 'weekend-day' : ''}">
            <div class="day-number">
              <span>${day.dayNumber} ${day.dayName}</span>
              ${day.isWeekend ? '<span class="weekend-indicator">WE</span>' : ''}
            </div>
            
            ${hasData ? `
              <div class="shift-hours">
                <div class="hours-section filled-hours">
                  Filled: ${formatHoursMinutes(dayData.filledMinutes)}
                  ${dayData.filledShifts > 0 ? `<span style="font-size: 9px; display: block;">${dayData.filledShifts} shift${dayData.filledShifts !== 1 ? 's' : ''}</span>` : ''}
                </div>
                <div class="hours-section available-hours">
                  Available: ${formatHoursMinutes(dayData.availableMinutes)}
                  ${dayData.availableShifts > 0 ? `<span style="font-size: 9px; display: block;">${dayData.availableShifts} shift${dayData.availableShifts !== 1 ? 's' : ''}</span>` : ''}
                </div>
                ${(dayData.filledMinutes + dayData.availableMinutes) > 0 ? `
                  <div class="total-hours">
                    Total: ${formatHoursMinutes(dayData.filledMinutes + dayData.availableMinutes)}
                  </div>
                ` : ''}
              </div>
            ` : `
              <div class="no-data">No shifts</div>
            `}
          </div>
        `;
      });
    });
    
    html += `
      </div>
      
      <div class="summary-section">
        <div class="summary-title">Monthly Summary</div>
        
        <div class="summary-grid">
          <div class="summary-card summary-total">
            <div class="summary-label">Total Shifts</div>
            <div class="summary-number">${monthTotalShifts}</div>
            <div class="summary-label">Across ${monthInfo.daysInMonth} days</div>
          </div>
          
          <div class="summary-card summary-filled">
            <div class="summary-label">Filled Hours</div>
            <div class="summary-number">${formatHoursMinutes(monthTotalFilledMinutes)}</div>
            <div class="summary-label">${Math.round(monthTotalFilledMinutes/60)} total hours</div>
          </div>
          
          <div class="summary-card summary-available">
            <div class="summary-label">Available Hours</div>
            <div class="summary-number">${formatHoursMinutes(monthTotalAvailableMinutes)}</div>
            <div class="summary-label">${Math.round(monthTotalAvailableMinutes/60)} total hours</div>
          </div>
        </div>
        
        <div class="coverage-rate">
          <div class="summary-label">Coverage Rate</div>
          <div class="coverage-number">${coverageRate}%</div>
          <div class="summary-label">of total hours are filled</div>
        </div>
      </div>
      
      <div class="legend">
        <div class="legend-title">Color Legend:</div>
        <div class="legend-item">
          <span class="legend-color" style="background-color: #4caf50;"></span>
          <span>Filled Hours (Assigned to staff)</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background-color: #2196f3;"></span>
          <span>Available Hours (Need assignment)</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background-color: rgba(255, 152, 0, 0.1);"></span>
          <span>Weekend Days (Saturday & Sunday)</span>
        </div>
      </div>
      
      <div class="footer">
        Beaumont Hospital Calendar Analytics System<br>
        Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}<br>
        For questions, contact: beaumontNursingBank@beaumont.ie
      </div>
    `;
    
    return html;
  };
  
  // Generate yearly view print content
  const generateYearlyPrintContent = (filterInfo) => {
    // Calculate yearly totals
    let yearlyTotalFilledMinutes = 0;
    let yearlyTotalAvailableMinutes = 0;
    let yearlyTotalShifts = 0;
    
    for (let i = 0; i < 12; i++) {
      const monthData = yearlyData[i] || { filledMinutes: 0, availableMinutes: 0, filledShifts: 0, availableShifts: 0 };
      yearlyTotalFilledMinutes += monthData.filledMinutes;
      yearlyTotalAvailableMinutes += monthData.availableMinutes;
      yearlyTotalShifts += monthData.filledShifts + monthData.availableShifts;
    }
    
    const coverageRate = yearlyTotalFilledMinutes + yearlyTotalAvailableMinutes > 0 
      ? Math.round((yearlyTotalFilledMinutes / (yearlyTotalFilledMinutes + yearlyTotalAvailableMinutes)) * 100)
      : 0;
    
    let html = `
      <div class="print-header">
        <div class="print-title">Yearly Calendar Analytics</div>
        <div class="print-subtitle">${selectedYear}</div>
        <div class="print-info">Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
        <div class="print-info">Generated By: ${userInfo?.name || 'Manager'}</div>
        ${filterInfo !== 'No filters applied' ? `<div class="print-info">Filters: ${filterInfo}</div>` : ''}
        <div class="print-info">View Mode: Yearly Overview</div>
      </div>
      
      <div class="year-grid">
    `;
    
    // Add month cards
    monthNames.forEach((monthName, index) => {
      const monthData = yearlyData[index] || { filledMinutes: 0, availableMinutes: 0, filledShifts: 0, availableShifts: 0 };
      const totalMinutes = monthData.filledMinutes + monthData.availableMinutes;
      const hasData = totalMinutes > 0;
      
      html += `
        <div class="month-card">
          <div class="month-header">${monthName}</div>
          
          ${hasData ? `
            <div class="month-stats">
              <div class="stat-row filled-stat">
                <span>Filled:</span>
                <span>${formatHoursMinutes(monthData.filledMinutes)}</span>
              </div>
              <div class="stat-row available-stat">
                <span>Available:</span>
                <span>${formatHoursMinutes(monthData.availableMinutes)}</span>
              </div>
              <div class="stat-row total-stat">
                <span>Total:</span>
                <span>${formatHoursMinutes(totalMinutes)}</span>
              </div>
              ${(monthData.filledShifts + monthData.availableShifts) > 0 ? `
                <div style="font-size: 9px; margin-top: 8px; text-align: center; color: #666;">
                  ${monthData.filledShifts + monthData.availableShifts} total shifts
                </div>
              ` : ''}
            </div>
          ` : `
            <div class="no-data" style="text-align: center; color: #999; font-style: italic; margin-top: 20px;">
              No shift data
            </div>
          `}
        </div>
      `;
    });
    
    html += `
      </div>
      
      <div class="summary-section">
        <div class="summary-title">Yearly Summary - ${selectedYear}</div>
        
        <div class="summary-grid">
          <div class="summary-card summary-total">
            <div class="summary-label">Total Shifts</div>
            <div class="summary-number">${yearlyTotalShifts}</div>
            <div class="summary-label">Across 12 months</div>
          </div>
          
          <div class="summary-card summary-filled">
            <div class="summary-label">Filled Hours</div>
            <div class="summary-number">${formatHoursMinutes(yearlyTotalFilledMinutes)}</div>
            <div class="summary-label">${Math.round(yearlyTotalFilledMinutes/60)} total hours</div>
          </div>
          
          <div class="summary-card summary-available">
            <div class="summary-label">Available Hours</div>
            <div class="summary-number">${formatHoursMinutes(yearlyTotalAvailableMinutes)}</div>
            <div class="summary-label">${Math.round(yearlyTotalAvailableMinutes/60)} total hours</div>
          </div>
        </div>
        
        <div class="coverage-rate">
          <div class="summary-label">Yearly Coverage Rate</div>
          <div class="coverage-number">${coverageRate}%</div>
          <div class="summary-label">of total hours are filled</div>
        </div>
      </div>
      
      <div class="legend">
        <div class="legend-title">Color Legend:</div>
        <div class="legend-item">
          <span class="legend-color" style="background-color: #4caf50;"></span>
          <span>Filled Hours (Assigned to staff)</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background-color: #2196f3;"></span>
          <span>Available Hours (Need assignment)</span>
        </div>
      </div>
      
      <div class="footer">
        Beaumont Hospital Calendar Analytics System<br>
        Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}<br>
        For questions, contact: beaumontNursingBank@beaumont.ie
      </div>
    `;
    
    return html;
  };
  
  const getFilterSummary = () => {
    const filters = [];
    if (selectedDirectorateFilter && selectedDirectorateFilter !== 'all') {
      const wardCount = filteredWards.length - 1;
      filters.push(`Directorate: ${selectedDirectorateFilter} (${wardCount} ward${wardCount !== 1 ? 's' : ''})`);
    }
    if (selectedWardFilter !== 'all') filters.push(`Ward: ${selectedWardFilter}`);
    if (selectedJobTitleFilter !== 'all') filters.push(`Job: ${selectedJobTitleFilter}`);
    return filters.length > 0 ? filters.join(' • ') : 'No filters applied';
  };
  
  // Format day display for monthly view
  const formatDayDisplay = (day) => {
    if (!day.isInMonth) {
      return (
        <Box sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'text.disabled',
          fontStyle: 'italic'
        }}>
          <Typography variant="caption" color="inherit">
            {day.dayNumber}
          </Typography>
          <Typography variant="caption" color="inherit" sx={{ fontSize: '0.6rem' }}>
            Not in month
          </Typography>
        </Box>
      );
    }
    
    const dayData = monthlyData[day.date] || {
      filledMinutes: 0,
      availableMinutes: 0,
      filledShifts: 0,
      availableShifts: 0
    };
    
    const hasFilled = dayData.filledMinutes > 0;
    const hasAvailable = dayData.availableMinutes > 0;
    
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 0.5,
          p: 0.5,
          bgcolor: day.isWeekend ? 'rgba(255, 152, 0, 0.1)' : 'transparent',
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Typography variant="caption" fontWeight="bold">
            {day.dayNumber} {day.dayName}
          </Typography>
          {day.isWeekend && (
            <Chip 
              label="WE" 
              size="small" 
              sx={{ 
                height: 16, 
                fontSize: '0.6rem',
                bgcolor: 'warning.light',
                color: 'warning.contrastText'
              }} 
            />
          )}
        </Box>
        
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, p: 0.5 }}>
          <Tooltip title={`${dayData.filledShifts} filled shift(s)`} arrow>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 0.5,
              borderRadius: 0.5,
              bgcolor: hasFilled ? 'success.light' : 'grey.100',
              color: hasFilled ? 'success.contrastText' : 'text.secondary',
              minHeight: '24px'
            }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                Filled
              </Typography>
              <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                {formatHoursMinutes(dayData.filledMinutes)}
              </Typography>
            </Box>
          </Tooltip>
          
          <Tooltip title={`${dayData.availableShifts} available shift(s)`} arrow>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 0.5,
              borderRadius: 0.5,
              bgcolor: hasAvailable ? 'info.light' : 'grey.100',
              color: hasAvailable ? 'info.contrastText' : 'text.secondary',
              minHeight: '24px'
            }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                Available
              </Typography>
              <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                {formatHoursMinutes(dayData.availableMinutes)}
              </Typography>
            </Box>
          </Tooltip>
          
          {(hasFilled || hasAvailable) && (
            <Box sx={{ 
              mt: 0.5,
              pt: 0.5,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                Total
              </Typography>
              <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.65rem' }}>
                {formatHoursMinutes(dayData.filledMinutes + dayData.availableMinutes)}
              </Typography>
            </Box>
          )}
          
          {!hasFilled && !hasAvailable && (
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                No shifts
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };
  
  // Format month display for yearly view
  const formatMonthDisplay = (monthIndex, monthData) => {
    const monthName = monthNames[monthIndex];
    const hasFilled = monthData.filledMinutes > 0;
    const hasAvailable = monthData.availableMinutes > 0;
    
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 0.5,
          p: 1,
          bgcolor: 'primary.light',
          color: 'white',
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Typography variant="subtitle2" fontWeight="bold">
            {monthName}
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => handleMonthSelect(monthIndex)}
            sx={{ color: 'white' }}
          >
            <CalendarToday fontSize="small" />
          </IconButton>
        </Box>
        
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
          <Tooltip title={`${monthData.filledShifts} filled shift(s)`} arrow>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1,
              borderRadius: 1,
              bgcolor: hasFilled ? 'success.light' : 'grey.100',
              color: hasFilled ? 'success.contrastText' : 'text.secondary',
              minHeight: '32px'
            }}>
              <Typography variant="body2">
                Filled
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatHoursMinutes(monthData.filledMinutes)}
              </Typography>
            </Box>
          </Tooltip>
          
          <Tooltip title={`${monthData.availableShifts} available shift(s)`} arrow>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1,
              borderRadius: 1,
              bgcolor: hasAvailable ? 'info.light' : 'grey.100',
              color: hasAvailable ? 'info.contrastText' : 'text.secondary',
              minHeight: '32px'
            }}>
              <Typography variant="body2">
                Available
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatHoursMinutes(monthData.availableMinutes)}
              </Typography>
            </Box>
          </Tooltip>
          
          {(hasFilled || hasAvailable) && (
            <Box sx={{ 
              mt: 1,
              pt: 1,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Typography variant="body2" fontWeight="bold">
                Total
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatHoursMinutes(monthData.filledMinutes + monthData.availableMinutes)}
              </Typography>
            </Box>
          )}
          
          <Button 
            variant="outlined" 
            size="small" 
            onClick={() => handleMonthSelect(monthIndex)}
            sx={{ mt: 'auto' }}
          >
            View Details
          </Button>
        </Box>
      </Box>
    );
  };
  
  // Handle layout fluid setting
  useEffect(() => {
    setFluid(true);
    return () => {
      setFluid(false);
    };
  }, [setFluid]);
  
  const isLoading = loadingMonthlyShifts || loadingYearlyShifts || loadingStaff || loadingHospitalLocations;
  
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Add Print Styles */}
      <PrintStyles />
      
      {/* Navigation Breadcrumbs */}
      <Breadcrumbs 
        aria-label="breadcrumb" 
        separator={<NavigateNext fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
        >
          Dashboard
        </Link>
        <Typography color="text.primary">Calendar Analytics</Typography>
      </Breadcrumbs>
      
      {/* Header with title and buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          Calendar Analytics
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Print Button */}
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={{
              bgcolor: '#673ab7',
              '&:hover': {
                bgcolor: '#5e35b1'
              }
            }}
            className="no-print"
          >
            Print Calendar
          </Button>
          
          {/* Staff Weekly View Button */}
          <Button
            variant="contained"
            startIcon={<Groups />}
            onClick={handleNavigateToStaffWeeklyViewFromHeader}
            sx={{
              bgcolor: '#1976d2',
              '&:hover': {
                bgcolor: '#1565c0'
              }
            }}
            className="no-print"
          >
            Ward Weekly View
          </Button>
        </Box>
      </Box>
      
      <Paper elevation={1} sx={{ mb: 2 }} className="no-print">
        <Tabs 
          value={viewMode} 
          onChange={(e, newValue) => setViewMode(newValue)}
          centered
        >
          <Tab 
            icon={<CalendarViewMonth />} 
            label="Year View" 
            value="year" 
            iconPosition="start"
          />
          <Tab 
            icon={<CalendarToday />} 
            label="Month View" 
            value="month" 
            iconPosition="start"
          />
        </Tabs>
      </Paper>
      
      {/* Top Row: Year/Month Selection, Filters, and Yearly Summary - Side by Side */}
      <Grid container spacing={2} sx={{ mb: 3 }} className="no-print">
        {/* Year/Month Selection - First Column */}
        <Grid item xs={4}>
          <Paper elevation={1} sx={{ p: 2, height: '100%', bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Year/Month Selection
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              {viewMode === 'year' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <IconButton onClick={() => navigateYear(-1)} size="small">
                    <ArrowBackIos fontSize="small" />
                  </IconButton>
                  <Typography variant="h6">
                    {selectedYear}
                  </Typography>
                  <IconButton onClick={() => navigateYear(1)} size="small">
                    <ArrowForwardIos fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <IconButton onClick={() => navigateMonth(-1)} size="small">
                    <ArrowBackIos fontSize="small" />
                  </IconButton>
                  <Typography variant="h6">
                    {monthInfo.monthName} {monthInfo.year}
                  </Typography>
                  <IconButton onClick={() => navigateMonth(1)} size="small">
                    <ArrowForwardIos fontSize="small" />
                  </IconButton>
                </Box>
              )}
              
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel>Year</InputLabel>
                <Select
                  value={selectedYear}
                  label="Year"
                  onChange={(e) => {
                    setSelectedYear(e.target.value);
                    if (viewMode === 'year') {
                      setSelectedMonth(null);
                    }
                  }}
                >
                  {generateYears().map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {viewMode === 'month' && (
                <FormControl fullWidth size="small">
                  <InputLabel>Month</InputLabel>
                  <Select
                    value={selectedMonth}
                    label="Month"
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  >
                    {monthNames.map((month, index) => (
                      <MenuItem key={month} value={index}>{month}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              {viewMode === 'year' ? (
                <Button 
                  startIcon={<Today />} 
                  onClick={goToCurrentYear}
                  variant="outlined"
                  size="small"
                  fullWidth
                >
                  Current Year
                </Button>
              ) : (
                <Button 
                  startIcon={<Today />} 
                  onClick={goToCurrentMonth}
                  variant="outlined"
                  size="small"
                  fullWidth
                >
                  Current Month
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>
        
        {/* Filters - Second Column */}
        <Grid item xs={4}>
          <Paper elevation={1} sx={{ p: 2, height: '100%', bgcolor: '#f5f5f5' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterAlt color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">
                  Filters
                </Typography>
              </Box>
              
              <Button 
                size="small" 
                onClick={handleClearFilters}
                disabled={(!selectedDirectorateFilter || selectedDirectorateFilter === 'all') && selectedWardFilter === 'all' && selectedJobTitleFilter === 'all'}
                startIcon={<FilterAltOff />}
              >
                Clear
              </Button>
            </Box>
            
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel>Directorate</InputLabel>
              <Select
                value={selectedDirectorateFilter || 'all'}
                label="Directorate"
                onChange={(e) => setSelectedDirectorateFilter(e.target.value)}
                startAdornment={<FilterAlt fontSize="small" sx={{ mr: 1 }} />}
                disabled={isDirectorateDisabled} // Add disabled prop here
              >
                {directorates.map(directorate => (
                  <MenuItem key={directorate} value={directorate}>
                    {directorate === 'all' ? 'All Directorates' : directorate}
                  </MenuItem>
                ))}
              </Select>
              {isDirectorateDisabled && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Directorate filter is locked to your assigned directorate
                </Typography>
              )}
            </FormControl>
            
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel>Ward</InputLabel>
              <Select
                value={selectedWardFilter}
                label="Ward"
                onChange={(e) => setSelectedWardFilter(e.target.value)}
                startAdornment={<FilterAlt fontSize="small" sx={{ mr: 1 }} />}
                disabled={filteredWards.length <= 1}
              >
                {filteredWards.map(ward => (
                  <MenuItem key={ward} value={ward}>
                    {ward === 'all' ? 
                      `All Wards${selectedDirectorateFilter && selectedDirectorateFilter !== 'all' ? ` (${filteredWards.length - 1})` : ''}` : 
                      ward
                    }
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth size="small">
              <InputLabel>Job Title</InputLabel>
              <Select
                value={selectedJobTitleFilter}
                label="Job Title"
                onChange={(e) => setSelectedJobTitleFilter(e.target.value)}
                startAdornment={<FilterAlt fontSize="small" sx={{ mr: 1 }} />}
              >
                {jobTitles.map(title => (
                  <MenuItem key={title} value={title}>
                    {title === 'all' ? 'All Job Titles' : title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Filter Summary */}
            <Box sx={{ mt: 2, p: 1, bgcolor: 'white', borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Active Filters:</strong> {getFilterSummary()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {viewMode === 'year' 
                  ? `Showing data for entire ${selectedYear}` 
                  : `Showing ${monthInfo?.daysInMonth} days in ${monthInfo?.monthName}`}
              </Typography>
            </Box>
          </Paper>
        </Grid>
        
        {/* Yearly Summary - Third Column */}
        <Grid item xs={4}>
          <Paper elevation={1} sx={{ p: 2, height: '100%', bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {viewMode === 'year' ? 'Yearly' : 'Monthly'} Summary
            </Typography>
            
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <Card sx={{ bgcolor: 'primary.light', color: 'white' }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                      Total Shifts
                    </Typography>
                    <Typography variant="h5">
                      {totalShifts}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      {viewMode === 'year' 
                        ? `Across 12 months` 
                        : `Across ${monthInfo?.daysInMonth} days`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={6}>
                <Card sx={{ bgcolor: 'success.light', color: 'white' }}>
                  <CardContent sx={{ p: 1 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                      Filled Hours
                    </Typography>
                    <Typography variant="h6">
                      {formatHoursMinutes(totalFilledHours)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={6}>
                <Card sx={{ bgcolor: 'info.light', color: 'white' }}>
                  <CardContent sx={{ p: 1 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                      Available Hours
                    </Typography>
                    <Typography variant="h6">
                      {formatHoursMinutes(totalAvailableHours)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Coverage Rate
              </Typography>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {Math.round((totalFilledHours / (totalFilledHours + totalAvailableHours)) * 100)}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                of total hours are filled
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      <Paper elevation={3} sx={{ p: 2 }} className="print-page">
        <Typography variant="h6" gutterBottom>
          {viewMode === 'year' 
            ? `Monthly Overview - ${selectedYear}`
            : `Daily Shift Hours - ${monthInfo?.monthName} ${monthInfo?.year}`}
        </Typography>
        
        <Alert severity="info" sx={{ mb: 2 }} className="no-print">
          <Typography variant="body2">
            {viewMode === 'year' 
              ? 'Each card shows filled (green) and available (blue) shift hours for that month. Click on a month to view daily details.'
              : 'Each cell shows filled (green) and available (blue) shift hours for that day. Hover over each section for detailed shift counts. Weekend days are highlighted in orange. Click on any day to open Staff Weekly View for that week.'}
          </Typography>
        </Alert>
        
        {viewMode === 'year' && (
          <Grid container spacing={2}>
            {monthNames.map((monthName, index) => {
              const monthData = yearlyData[index] || {
                filledMinutes: 0,
                availableMinutes: 0,
                filledShifts: 0,
                availableShifts: 0,
                filledHours: 0,
                availableHours: 0
              };
              
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index} className="print-page">
                  <Paper 
                    elevation={2}
                    sx={{ 
                      height: '200px',
                      border: 1,
                      borderColor: 'divider',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s'
                      }
                    }}
                    onClick={() => handleMonthSelect(index)}
                    className="no-print"
                  >
                    {formatMonthDisplay(index, monthData)}
                  </Paper>
                  
                  {/* Print version of month card */}
                  <div style={{ 
                    display: 'none',
                    '@media print': {
                      display: 'block',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '12px',
                      minHeight: '120px',
                      marginBottom: '12px'
                    }
                  }}>
                    <div style={{ 
                      backgroundColor: '#1976d2', 
                      color: 'white', 
                      padding: '8px', 
                      textAlign: 'center',
                      fontWeight: 'bold',
                      borderRadius: '3px',
                      marginBottom: '10px'
                    }}>
                      {monthName}
                    </div>
                    <div style={{ fontSize: '11px' }}>
                      <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        margin: '4px 0',
                        padding: '4px',
                        backgroundColor: '#4caf50',
                        color: 'white',
                        borderRadius: '3px'
                      }}>
                        <span>Filled:</span>
                        <span>{formatHoursMinutes(monthData.filledMinutes)}</span>
                      </div>
                      <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        margin: '4px 0',
                        padding: '4px',
                        backgroundColor: '#2196f3',
                        color: 'white',
                        borderRadius: '3px'
                      }}>
                        <span>Available:</span>
                        <span>{formatHoursMinutes(monthData.availableMinutes)}</span>
                      </div>
                      <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        margin: '4px 0',
                        padding: '4px',
                        backgroundColor: '#333',
                        color: 'white',
                        borderRadius: '3px',
                        fontWeight: 'bold'
                      }}>
                        <span>Total:</span>
                        <span>{formatHoursMinutes(monthData.filledMinutes + monthData.availableMinutes)}</span>
                      </div>
                      {(monthData.filledShifts + monthData.availableShifts) > 0 && (
                        <div style={{ fontSize: '9px', marginTop: '8px', textAlign: 'center', color: '#666' }}>
                          {monthData.filledShifts + monthData.availableShifts} total shifts
                        </div>
                      )}
                    </div>
                  </div>
                </Grid>
              );
            })}
          </Grid>
        )}
        
        {viewMode === 'month' && (
          <>
            <Grid container spacing={1}>
              {calendarDays.map((day) => (
                <Grid item xs={12} sm={6} md={4} lg={2.4} xl={1.7} key={day.dayNumber} className="print-page">
                  <Paper 
                    elevation={1}
                    sx={{ 
                      height: '140px',
                      border: 1,
                      borderColor: day.isInMonth ? 
                        (day.isWeekend ? 'warning.light' : 'divider') : 
                        'grey.300',
                      bgcolor: day.isInMonth ? 
                        (day.isWeekend ? 'rgba(255, 152, 0, 0.05)' : 'white') : 
                        'grey.50',
                      overflow: 'hidden',
                      position: 'relative',
                      cursor: day.isInMonth ? 'pointer' : 'default',
                      '&:hover': day.isInMonth ? {
                        boxShadow: 3,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s'
                      } : {}
                    }}
                    onClick={() => {
                      if (day.isInMonth && day.date) {
                        handleNavigateToStaffWeeklyView(day.date);
                      }
                    }}
                    className="no-print"
                  >
                    {/* Click to view week tooltip indicator */}
                    {day.isInMonth && (
                      <Tooltip title={`Click to view week starting with this day`} arrow>
                        <Box sx={{ 
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 1
                        }}>
                          <Groups fontSize="small" />
                        </Box>
                      </Tooltip>
                    )}
                    
                    {formatDayDisplay(day)}
                    
                    <Box sx={{ 
                      position: 'absolute',
                      top: 2,
                      left: 2,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: day.isWeekend ? 'warning.main' : 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography variant="caption" fontWeight="bold">
                        {day.dayNumber}
                      </Typography>
                    </Box>
                  </Paper>
                  
                  {/* Print version of day card */}
                  <div style={{ 
                    display: 'none',
                    '@media print': {
                      display: 'block',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '8px',
                      minHeight: '100px',
                      marginBottom: '4px',
                      backgroundColor: day.isInMonth ? (day.isWeekend ? 'rgba(255, 152, 0, 0.05)' : 'white') : '#f5f5f5'
                    }
                  }}>
                    <div style={{ 
                      fontWeight: 'bold', 
                      fontSize: '14px', 
                      marginBottom: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span>{day.dayNumber} {day.dayName}</span>
                      {day.isWeekend && (
                        <span style={{ 
                          backgroundColor: '#ff9800', 
                          color: 'white', 
                          padding: '2px 4px', 
                          borderRadius: '3px', 
                          fontSize: '9px',
                          fontWeight: 'bold'
                        }}>
                          WE
                        </span>
                      )}
                    </div>
                    
                    {day.isInMonth ? (
                      <>
                        {(() => {
                          const dayData = monthlyData[day.date] || { 
                            filledMinutes: 0, 
                            availableMinutes: 0,
                            filledShifts: 0,
                            availableShifts: 0
                          };
                          const hasData = dayData.filledMinutes > 0 || dayData.availableMinutes > 0;
                          
                          return hasData ? (
                            <>
                              <div style={{ 
                                backgroundColor: '#4caf50',
                                color: 'white',
                                padding: '4px',
                                borderRadius: '3px',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                margin: '4px 0'
                              }}>
                                <div>Filled: {formatHoursMinutes(dayData.filledMinutes)}</div>
                                {dayData.filledShifts > 0 && (
                                  <div style={{ fontSize: '9px' }}>{dayData.filledShifts} shift{dayData.filledShifts !== 1 ? 's' : ''}</div>
                                )}
                              </div>
                              <div style={{ 
                                backgroundColor: '#2196f3',
                                color: 'white',
                                padding: '4px',
                                borderRadius: '3px',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                margin: '4px 0'
                              }}>
                                <div>Available: {formatHoursMinutes(dayData.availableMinutes)}</div>
                                {dayData.availableShifts > 0 && (
                                  <div style={{ fontSize: '9px' }}>{dayData.availableShifts} shift{dayData.availableShifts !== 1 ? 's' : ''}</div>
                                )}
                              </div>
                              {(dayData.filledMinutes + dayData.availableMinutes) > 0 && (
                                <div style={{ 
                                  backgroundColor: '#333',
                                  color: 'white',
                                  padding: '4px',
                                  borderRadius: '3px',
                                  fontSize: '9px',
                                  fontWeight: 'bold',
                                  marginTop: '8px'
                                }}>
                                  Total: {formatHoursMinutes(dayData.filledMinutes + dayData.availableMinutes)}
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{ 
                              textAlign: 'center', 
                              color: '#999', 
                              fontStyle: 'italic', 
                              fontSize: '10px', 
                              marginTop: '20px'
                            }}>
                              No shifts
                            </div>
                          );
                        })()}
                      </>
                    ) : (
                      <div style={{ 
                        textAlign: 'center', 
                        color: '#999', 
                        fontStyle: 'italic', 
                        fontSize: '10px', 
                        marginTop: '20px'
                      }}>
                        Not in month
                      </div>
                    )}
                  </div>
                </Grid>
              ))}
            </Grid>
            
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }} className="no-print">
              <Typography variant="subtitle2" gutterBottom>
                Color Legend & Navigation:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, bgcolor: 'success.light', borderRadius: 1 }} />
                      <Typography variant="body2">Filled Hours (Assigned to staff)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, bgcolor: 'info.light', borderRadius: 1 }} />
                      <Typography variant="body2">Available Hours (Need assignment)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, bgcolor: 'warning.light', borderRadius: 1 }} />
                      <Typography variant="body2">Weekend Days (Saturday & Sunday)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        border: 1, 
                        borderColor: 'grey.400',
                        bgcolor: 'grey.100', 
                        borderRadius: 1 
                      }} />
                      <Typography variant="body2">Days not in current month</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                    <Box sx={{ 
                      width: 24, 
                      height: 24, 
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Groups fontSize="small" />
                    </Box>
                    <Typography variant="body2">
                      Click on any day cell to open Staff Weekly View for that week
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Paper>
      
      {totalShifts > 0 && (
        <Paper elevation={2} sx={{ p: 2, mt: 3 }} className="print-page">
          <Typography variant="h6" gutterBottom>
            {viewMode === 'year' ? 'Year' : 'Month'} Highlights
          </Typography>
          <Grid container spacing={2}>
            {viewMode === 'month' && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom color="primary">
                      🏆 Busiest Day
                    </Typography>
                    {(() => {
                      // FIX: Add null checks and ensure data exists
                      if (!calendarDays || !monthlyData || Object.keys(monthlyData).length === 0) {
                        return (
                          <Typography variant="body2" color="textSecondary">
                            No shift data available for this month
                          </Typography>
                        );
                      }
                      
                      // Find the first day in month for fallback
                      const firstDayInMonth = calendarDays.find(day => day.isInMonth && day.date);
                      
                      if (!firstDayInMonth) {
                        return (
                          <Typography variant="body2" color="textSecondary">
                            No days in this month
                          </Typography>
                        );
                      }
                      
                      // Filter days that are in month AND have date AND have data
                      const validDays = calendarDays.filter(day => 
                        day.isInMonth && day.date && monthlyData[day.date]
                      );
                      
                      if (validDays.length === 0) {
                        return (
                          <Typography variant="body2" color="textSecondary">
                            No shift data available for this month
                          </Typography>
                        );
                      }
                      
                      // Find busiest day
                      const busyDay = validDays.reduce((max, day) => {
                        const dayData = monthlyData[day.date];
                        const maxData = monthlyData[max.date];
                        
                        const totalMinutes = dayData.filledMinutes + dayData.availableMinutes;
                        const maxMinutes = maxData ? (maxData.filledMinutes + maxData.availableMinutes) : 0;
                        
                        return totalMinutes > maxMinutes ? day : max;
                      }, validDays[0]);
                      
                      const busyDayData = monthlyData[busyDay.date] || { 
                        filledMinutes: 0, 
                        availableMinutes: 0,
                        filledShifts: 0,
                        availableShifts: 0
                      };
                      
                      return (
                        <>
                          <Typography variant="body1" fontWeight="bold">
                            {busyDay.dayName}, {busyDay.dayNumber} {monthInfo?.monthName}
                          </Typography>
                          <Typography variant="body2">
                            Total hours: {formatHoursMinutes(busyDayData.filledMinutes + busyDayData.availableMinutes)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {busyDayData.filledShifts} filled shifts • {busyDayData.availableShifts} available shifts
                          </Typography>
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>
              </Grid>
            )}
            
            {viewMode === 'year' && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom color="primary">
                      🏆 Busiest Month
                    </Typography>
                    {(() => {
                      // FIX: Add null check for yearlyData
                      if (!yearlyData || Object.keys(yearlyData).length === 0) {
                        return (
                          <Typography variant="body2" color="textSecondary">
                            No shift data available for this year
                          </Typography>
                        );
                      }
                      
                      let busiestMonthIndex = 0;
                      let maxMinutes = 0;
                      
                      for (let i = 0; i < 12; i++) {
                        const monthData = yearlyData[i];
                        if (!monthData) continue;
                        
                        const totalMinutes = monthData.filledMinutes + monthData.availableMinutes;
                        if (totalMinutes > maxMinutes) {
                          maxMinutes = totalMinutes;
                          busiestMonthIndex = i;
                        }
                      }
                      
                      const busiestMonth = yearlyData[busiestMonthIndex] || {
                        filledMinutes: 0,
                        availableMinutes: 0,
                        filledShifts: 0,
                        availableShifts: 0
                      };
                      
                      if (maxMinutes === 0) {
                        return (
                          <Typography variant="body2" color="textSecondary">
                            No shift data available for any month
                          </Typography>
                        );
                      }
                      
                      return (
                        <>
                          <Typography variant="body1" fontWeight="bold">
                            {monthNames[busiestMonthIndex]}
                          </Typography>
                          <Typography variant="body2">
                            Total hours: {formatHoursMinutes(busiestMonth.filledMinutes + busiestMonth.availableMinutes)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {busiestMonth.filledShifts} filled shifts • {busiestMonth.availableShifts} available shifts
                          </Typography>
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>
              </Grid>
            )}
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    📅 Coverage Rate
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {Math.round((totalFilledHours / (totalFilledHours + totalAvailableHours)) * 100)}%
                  </Typography>
                  <Typography variant="body2">
                    of total hours are filled
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatHoursMinutes(totalFilledHours)} filled out of {formatHoursMinutes(totalFilledHours + totalAvailableHours)} total hours
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default MonthlyCalendarView;